/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { string, bool } from 'prop-types';

import { INTERCOM_REQUEST_OFFER_CLASS } from 'ggApp/modules/intercom';
import { Translator } from 'ggApp/modules/translator/components';
import { mixLinkOnNavEnabled as mixLinkOnNavEnabledSelector } from 'ggApp/modules/featureFlag/selectors';
import { getHIWPageUrl } from 'ggApp/utils/path';
import { Wrap, Link, Button } from './styled';

export const utmParams = '?utm_source=grover&utm_medium=referral';

const PageLinks = ({
    className,
    isDesktopUser,
    isTransparent,
    isLoggedIn,
    countryCode,
    storeCode,
    activeLanguage,
    isBusinessStore,
    isPartner,
    businessStoreDealsEnabled,
    requestOfferEnabled,
    mixLinkOnNavEnabled,
    b2cRedirectionModal,
    user,
}) => {
    let external = true;

    const storesWithCustomLP = ['us', 'nl', 'at', 'es'];
    const b2bSelfCheckoutEnabled = ['business'];

    const renderLandingPage = useCallback(() => {
        return isDesktopUser && storesWithCustomLP.includes(countryCode);
    }, [isDesktopUser, storesWithCustomLP, countryCode]);

    if (isPartner) {
        external = true;
    }

    const businessLinkOnClick = useCallback(
        (e) => {
            e.preventDefault();
            b2cRedirectionModal.openModal();
        },
        [b2cRedirectionModal],
    );

    const shouldShowRedirectionModal =
        isBusinessStore &&
        isLoggedIn &&
        user.default_store &&
        user.default_store.code.includes('business');

    const storeLink = useCallback(() => {
        if (!isDesktopUser || countryCode !== 'de') {
            return null;
        }

        const sLink = isBusinessStore
            ? `/de-${activeLanguage}`
            : `/business-${activeLanguage}/for-business`;

        return (
            <Link
                external
                pageLinkTo={sLink}
                pageLinkTk={
                    isBusinessStore ? 'OFFERINGS_LINKS_B2C_TEXT' : 'OFFERINGS_LINKS_B2B_TEXT'
                }
                onClick={shouldShowRedirectionModal ? businessLinkOnClick : undefined}
                type="countrySpecificLink"
            />
        );
    }, [
        isDesktopUser,
        countryCode,
        isBusinessStore,
        activeLanguage,
        shouldShowRedirectionModal,
        businessLinkOnClick,
    ]);

    const buildBusinessLinkProps = useCallback(() => {
        if (isBusinessStore) {
            return {
                id: 'customer-lp',
                tk: 'OFFERINGS_LINKS_B2C_TEXT',
                to: `/${countryCode}-${activeLanguage}`,
                external: true,
                target: '_self',
            };
        }
        const businessStore = countryCode === 'de' ? 'business' : `business_${countryCode}`;
        const hasLandingPage =
            countryCode === 'de' || b2bSelfCheckoutEnabled.includes(`business_${countryCode}`);

        return {
            id: 'business-lp',
            tk: 'OFFERINGS_LINKS_B2B_TEXT',
            to: hasLandingPage
                ? `/${businessStore}-${activeLanguage}/for-business`
                : `https://get-business.grover.com/${countryCode}-${activeLanguage}/${utmParams}`,
            external: hasLandingPage,
            target: hasLandingPage ? '_self' : '_blank',
        };
    }, [activeLanguage, b2bSelfCheckoutEnabled, countryCode, isBusinessStore]);

    const businessLink = useCallback(() => {
        if (renderLandingPage(countryCode, isDesktopUser)) {
            const linkProps = buildBusinessLinkProps();
            return (
                <Link
                    id={linkProps.id}
                    pageLinkTo={linkProps.to}
                    pageLinkTk={linkProps.tk}
                    external={linkProps.external}
                    type="countrySpecificLink"
                    target={linkProps.target}
                    onClick={shouldShowRedirectionModal ? businessLinkOnClick : undefined}
                />
            );
        }
        return null;
    }, [
        buildBusinessLinkProps,
        countryCode,
        isDesktopUser,
        renderLandingPage,
        businessLinkOnClick,
        shouldShowRedirectionModal,
    ]);

    return (
        <Wrap className={className} isTransparent={isTransparent}>
            {(isDesktopUser || !isLoggedIn) &&
                (!isBusinessStore || b2bSelfCheckoutEnabled.includes(storeCode)) && (
                    <Link
                        pageLinkTo={getHIWPageUrl(storeCode, activeLanguage)}
                        pageLinkTk="TIPS_LINKS_HIW_TEXT"
                        external={external}
                        type="countrySpecificLink"
                    />
                )}

            {(isDesktopUser || isLoggedIn) &&
                (!isBusinessStore || b2bSelfCheckoutEnabled.includes(storeCode)) && (
                    <Link pageLinkTo="/trending" pageLinkTk="OFFERINGS_LINKS_TOP_PRODUCTS_TEXT" />
                )}

            {isDesktopUser &&
                (!isBusinessStore || businessStoreDealsEnabled) &&
                (!isBusinessStore || b2bSelfCheckoutEnabled.includes(storeCode)) &&
                storeCode !== 'us' && (
                    <Link pageLinkTo="/deals" pageLinkTk="OFFERINGS_LINKS_DEALS_TEXT" />
                )}

            {mixLinkOnNavEnabled && isDesktopUser && !isBusinessStore && storeCode === 'de' && (
                <Link pageLinkTo="/mix" pageLinkTk="MIX_LANDING_NAV_TITLE" />
            )}

            {isDesktopUser && requestOfferEnabled && (
                <Button className={INTERCOM_REQUEST_OFFER_CLASS} type="button">
                    <Translator tk="OFFERINGS_LINKS_REQUEST_OFFER_TEXT" />
                </Button>
            )}

            {storeLink()}
            {businessLink()}
        </Wrap>
    );
};

PageLinks.propTypes = {
    className: string,
    isDesktopUser: bool,
    isLoggedIn: bool,
    countryCode: string,
    storeCode: string,
    activeLanguage: string,
    isBusinessStore: bool,
    isPartner: bool,
    businessStoreDealsEnabled: bool,
    requestOfferEnabled: bool,
    mixLinkOnNavEnabled: bool,
};

PageLinks.defaultProps = {
    className: '',
    isDesktopUser: false,
    isLoggedIn: false,
    countryCode: '',
    storeCode: '',
    activeLanguage: '',
    isBusinessStore: false,
    isPartner: false,
    requestOfferEnabled: false,
    businessStoreDealsEnabled: false,
    mixLinkOnNavEnabled: false,
};

const enhance = connect(
    createStructuredSelector({
        mixLinkOnNavEnabled: mixLinkOnNavEnabledSelector,
    }),
);

export default enhance(PageLinks);
